import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Box, Button, List, ListSubheader, Toolbar } from '@mui/material';
import { BrandLogo, LegalLinks, ReportIssue } from '@components';
import { LinkDrawer } from './LinkDrawer';
import { UserProfile } from './UserProfile';
import StarIcon from '@mui/icons-material/Star';
import ContactsRoundedIcon from '@mui/icons-material/ContactsRounded';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import NextWeekOutlinedIcon from '@mui/icons-material/NextWeekOutlined';
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';

import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { authSelectors, logoutUser } from '@redux/auth';

import { countActiveCandidates, isSamePage } from '@utils';
import { AppRoutes, CdPermission, hasPermission } from '@constants';
import { logEvent } from '@services';
import { AccountSelector } from './AccountSelector';
import { selectPermissions } from '@redux/accounts/accounts.selectors';
import { posiitonsSelectors } from '@redux/positions';
import { navRailBreakpoint } from '@theme/variables';

interface IProps {
  onNavigate?: () => void;
}

export const DrawerContent: React.FC<IProps> = ({ onNavigate }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const permissions = useAppSelector(selectPermissions);
  const location = useLocation();

  const ACL = {
    TEAMS: 'T:R',
    CANDIDATES: 'H:R',
  } as const satisfies Record<string, CdPermission>;

  const can = <K extends keyof typeof ACL>(k: K) =>
    hasPermission(permissions, ACL[k]);

  const { data: profile } = useAppSelector(authSelectors.getProfileAPIData);
  const { data: positions } = useAppSelector(
    posiitonsSelectors.getPositionsData,
  );
  const activeCandidateCount = useMemo(
    () => countActiveCandidates(positions),
    [positions],
  );
  const { list: accountList, selected: selectedAccountId } = useAppSelector(
    (state) => state.accounts,
  );
  const [isReportIssueModalOpen, setIsReportIssueModalOpen] = useState(false);
  const selectedAccountName =
    selectedAccountId && accountList.length
      ? accountList.find((account) => account.fssId === selectedAccountId)
          ?.name || null
      : null;

  const onOpenReportIssueModal = useCallback(() => {
    setIsReportIssueModalOpen(true);
    logEvent('report_issue_opened');
  }, []);

  const onCloseReportIssueModal = useCallback(
    () => setIsReportIssueModalOpen(false),
    [],
  );

  const handleLogout = useCallback(async () => {
    await dispatch(logoutUser());
    navigate(`/${AppRoutes.SIGN_IN}`);
  }, [dispatch, navigate]);

  const createLinkDrawer = (
    Icon: React.ElementType,
    label: string,
    href: string,
    disabled = false,
    eventName?: string,
    count?: number,
  ) => {
    return (
      !disabled && (
        <LinkDrawer
          Icon={Icon}
          isActive={isSamePage(location.pathname, href)}
          label={label}
          href={href}
          disabled={disabled}
          count={count}
          onClick={() => {
            if (!isSamePage(location.pathname, href)) {
              eventName && logEvent(eventName);
            }
            onNavigate && onNavigate();
          }}
        />
      )
    );
  };

  return (
    <>
      <Box display="flex" flexDirection="column" px={1} py={2} height="100%">
        <Toolbar
          sx={{ flexShrink: 0, display: { [navRailBreakpoint]: 'none' } }}
        />
        <Box
          flexShrink={0}
          sx={{
            display: { xs: 'none', [navRailBreakpoint]: 'block' },
            marginLeft: 2,
          }}
        >
          <BrandLogo />
        </Box>
        {/* minHeight 0: a flex item defaults to min-height auto and will not shrink below its
            content, which leaves overflow here inert and scrolls the whole drawer instead. */}
        <Box
          flex={1}
          minHeight={0}
          overflow="auto"
          sx={{ overscrollBehavior: 'contain' }}
        >
          <List component="nav">
            <ListSubheader disableSticky>People</ListSubheader>
            {createLinkDrawer(
              StarIcon,
              'My teams',
              `/${AppRoutes.TEAMS}`,
              !can('TEAMS'),
              'myteams_reviewed',
            )}
            {createLinkDrawer(
              ContactsRoundedIcon,
              'Candidates',
              `/${AppRoutes.CANDIDATES}`,
              !can('CANDIDATES'),
              'candidates_reviewed',
              positions.length ? activeCandidateCount : undefined,
            )}

            {/* <ListSubheader disableSticky>Budgets</ListSubheader> */}
            {createLinkDrawer(
              AccessTimeIcon,
              'Billed hours',
              `/${AppRoutes.BILLED_HOURS}`,
              true, // !can('BILLED_HOURS'),
              'billedhours_reviewed',
            )}
            {createLinkDrawer(
              NextWeekOutlinedIcon,
              'SOWs',
              `/${AppRoutes.SOW}`,
              true, // !can('SOW'),
              'sow_reviewed',
            )}

            <ListSubheader disableSticky>Feedback</ListSubheader>
            {createLinkDrawer(
              InsertEmoticonIcon,
              'Satisfaction surveys',
              `/${AppRoutes.SURVEYS}`,
              true, // !can('SURVEYS'),
              'satisfaction_survey_reviewed',
            )}
            <LinkDrawer
              Icon={ChatOutlinedIcon}
              label="Report issue"
              onClick={onOpenReportIssueModal}
            />
          </List>
        </Box>

        <Box
          display="flex"
          flexDirection="column"
          alignItems="start"
          flexShrink={0}
          gap={1}
          marginLeft={2}
          marginTop={2}
        >
          <UserProfile
            profile={profile}
            companyNameOverride={selectedAccountName}
          />
          <Button
            variant="text"
            color="info"
            size="small"
            onClick={handleLogout}
          >
            Log out
          </Button>
          <Box width="100%" pr={2}>
            <AccountSelector />
          </Box>
          <LegalLinks />
        </Box>
      </Box>
      <ReportIssue
        isOpen={isReportIssueModalOpen}
        onClose={onCloseReportIssueModal}
      />
    </>
  );
};
